import axios from "axios";
import moment from "moment";
import { formatMoneda } from "../../../../../util/varios";

export const publicidadService = {
  // ============= FUNCIÓN BASE =============
  async apiCall(endpoint, token) {
    try {
      const response = await axios.get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
          "Content-Type": "application/json",
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // ============= MÉTODO PRINCIPAL UNIFICADO =============

  // Obtener todos los datos de publicidad (comercio, persona, deudas, config, valorut)
  async getPublicidad(
    idcomercio,
    token,
    conexion,
    fechaSistema,
    espiritu,
    factorCambio,
  ) {
    // 1. Obtener datos del comercio
    const comercioEndpoint = `${conexion}/api/consulta/modeli?campos=*&id=${idcomercio}&nomtab=comercio&nomid=idcomercio`;
    const comercioResponse = await this.apiCall(comercioEndpoint, token);

    if (comercioResponse.status !== 200 || !comercioResponse.data?.idcomercio) {
      throw new Error("No se encontró el comercio");
    }

    const comercioData = comercioResponse.data;

    // 2. Obtener datos de la persona (representante legal)
    let personaData = null;
    if (comercioData.reprelegal && comercioData.reprelegal !== "0") {
      const personaEndpoint = `${conexion}/api/consulta/modeli?campos=*&id=${comercioData.reprelegal}&nomtab=persona&nomid=cedula`;
      const personaResponse = await this.apiCall(personaEndpoint, token);
      if (personaResponse.status === 200 && personaResponse.data?.cedula > 0) {
        personaData = personaResponse.data;
      }
    }

    // 3. Obtener deudas de publicidad
    const deudaEndpoint = `${conexion}/api/publicidad/consultadeuda?nomtab=publicidad&nomid=idcomercio&id=${idcomercio}`;
    const deudaResponse = await this.apiCall(deudaEndpoint, token);

    // 4. Obtener configuración
    const configEndpoint = `${conexion}/api/comun/seleccionaregistros?campos=*&nomtab=configindustria&orden=1`;
    const configResponse = await this.apiCall(configEndpoint, token);

    let configData = null;
    let valorut = 0;

    if (configResponse.status === 200 && configResponse.data?.length > 0) {
      configData = configResponse.data[0];

      // 5. Obtener valor UT si existe factorcalculo
      if (configData.factorcalculo) {
        try {
          const valorutResponse = await this.getValorUT(
            configData.factorcalculo,
            fechaSistema,
            espiritu,
            token,
            conexion,
          );
          if (
            valorutResponse.status === 200 &&
            valorutResponse.data?.length > 0
          ) {
            valorut = valorutResponse.data[0].valor;
          }
        } catch (error) {
          console.error("Error al obtener valor UT:", error);
        }
      }
    }

    // 6. Obtener factor de cambio
    let factorCambioValor = factorCambio;
    if (!factorCambioValor) {
      try {
        const factorEndpoint = `${conexion}/api/vehiculo/factorcambio`;
        const factorResponse = await this.apiCall(factorEndpoint, token);
        if (factorResponse.status === 200) {
          factorCambioValor = factorResponse.data.valor;
        }
      } catch (error) {
        console.error("Error al obtener factor cambio:", error);
      }
    }

    // 7. Procesar deudas
    const { deuda, history, total } = this.mapDeudasPublicidad(
      deudaResponse.data || [],
    );

    // 8. Retornar todos los datos mapeados
    return {
      status: 200,
      mappedData: {
        comercio: this.mapComercioData(comercioData),
        persona: this.mapPersonaData(personaData),
        deuda: deuda,
        history: history,
        total: total,
        recargo: configData,
        valorut: valorut,
        factorCambio: factorCambioValor,
      },
    };
  },

  // ============= MÉTODOS ADICIONALES =============

  // Obtener lista de comercios
  async getComercios(token, conexion) {
    const campos = "*";
    const nomtab = "comercio";
    const orden = "idcomercio";
    const endpoint = `${conexion}/api/comun/seleccionaregistros?campos=${campos}&nomtab=${nomtab}&orden=${orden}`;
    return await this.apiCall(endpoint, token);
  },

  // Obtener valor UT
  async getValorUT(factorcalculo, fechaSistema, espiritu, token, conexion) {
    const tipo = factorcalculo.toString();
    const m = parseInt(fechaSistema.substring(5, 7));
    const a = parseInt(fechaSistema.substring(0, 4));
    const endpoint = `${conexion}/api/publicidad/valorut?factorcalculo=${factorcalculo}&anio=${a}&mes=${m}&espiritu=${espiritu}`;
    return await this.apiCall(endpoint, token);
  },

  // Obtener factor de cambio
  async getFactorCambio(token, conexion) {
    const endpoint = `${conexion}/api/vehiculo/factorcambio`;
    return await this.apiCall(endpoint, token);
  },

  // Obtener configuración
  async getConfiguracion(token, conexion) {
    const endpoint = `${conexion}/api/comun/seleccionaregistros?campos=*&nomtab=configindustria&orden=1`;
    return await this.apiCall(endpoint, token);
  },

  // ============= MAPEADORES =============

  // Mapear datos del comercio
  mapComercioData(data) {
    if (!data) return null;
    return {
      idcomercio: data.idcomercio,
      ritm: data.ritm?.trim().replace(/\s+/g, " ") || "",
      rif: data.rif?.trim().replace(/\s+/g, " ") || "",
      nombre: data.nombre?.trim().replace(/\s+/g, " ") || "",
      codigocatastral: data.codigocatastral?.trim() || "",
      reprelegal: data.reprelegal,
    };
  },

  // Mapear datos de la persona
  mapPersonaData(data) {
    if (!data) return null;
    return {
      cedula: data.cedula,
      nombres: data.nombres?.trim().replace(/\s+/g, " ") || "",
      apellidos: data.apellidos?.trim().replace(/\s+/g, " ") || "",
      direccion: data.direccion?.trim().replace(/\s+/g, " ") || "",
      tlf1: data.tlf1,
      tlf2: data.tlf2,
      correoe: data.correoe,
    };
  },

  // Mapear deudas de publicidad
  mapDeudasPublicidad(data) {
    if (!data || data.length === 0) return { deuda: [], history: [], total: 0 };

    const deuda = [];
    const history = [];
    let desc = 0;
    let total = 0;

    data.forEach((item) => {
      const mes = this.getMesNombre(item.fechainscripcion);

      const deudaBase = {
        idpublicidad: item.idpublicidad,
        idcomercio: item.idcomercio,
        idtipopublicidad: item.idtipopublicidad,
        ancho: item.ancho,
        alto: item.alto,
        cantidad: item.cantidad,
        monto: item.monto,
        anio: item.anio,
        cancelada: item.cancelada,
        fechainscripcion: item.fechainscripcion,
        ubicacion: item.ubicacion,
        fechapago: item.fechapago,
        numerorecibo: item.numerorecibo,
        reccroquisubi: item.reccroquisubi,
        recplanoestructura: item.recplanoestructura,
        recautpropieinmue: item.recautpropieinmue,
        recautinsvialreg: item.recautinsvialreg,
        recusoconfddu: item.recusoconfddu,
        descripcion: item.descripcion,
        codigoordenanza: item.codigoordenanza,
        mes: mes,
      };

      if (!item.cancelada) {
        desc += parseFloat(item.monto);
        total = formatMoneda(desc.toString().replace(".", ","), ",", ".", 2);
        deuda.push(deudaBase);
      } else {
        history.push(deudaBase);
      }
    });

    return { deuda, history, total };
  },

  // Obtener nombre del mes
  getMesNombre(fecha) {
    if (!fecha) return "";
    const mm = parseInt(moment.utc(fecha).format("YYYY-MM-DD").substring(5, 7));
    const meses = [
      "ENERO",
      "FEBRERO",
      "MARZO",
      "ABRIL",
      "MAYO",
      "JUNIO",
      "JULIO",
      "AGOSTO",
      "SEPTIEMBRE",
      "OCTUBRE",
      "NOVIEMBRE",
      "DICIEMBRE",
    ];
    return meses[mm - 1] || "";
  },
};
